import {
  EGlobalSearchTargetKind,
  type IGlobalSearchStaticEntry,
} from "../types/global-search";
import { EGlobalSearchGate } from "../types/global-search-index";
import { buildGlobalSearchPageEntry } from "./global-search-index-builders";
import {
  AGENTS_TAB_VALUES,
  ANALYTICS_TAB_VALUES,
  INTEGRATIONS_TAB_VALUES,
  KNOWLEDGE_TAB_VALUES,
  SETTINGS_SECTION_VALUES,
} from "./navigation-tabs";

const ANALYTICS_TITLE_KEY = "sidebar:menu.analytics";
const AGENTS_TITLE_KEY = "sidebar:menu.agents";
const INTEGRATIONS_TITLE_KEY = "sidebar:menu.integrations";
const KNOWLEDGE_TITLE_KEY = "sidebar:menu.knowledge";

const ANALYTICS_GATES = [
  EGlobalSearchGate.IN_ORGANIZATION,
  EGlobalSearchGate.ANALYTICS_PAGE,
] as const;
const AGENTS_GATES = [
  EGlobalSearchGate.AGENTS_ENABLED,
  EGlobalSearchGate.AGENTS_READ,
] as const;
const CALL_ASSISTANT_GATES = [
  EGlobalSearchGate.CALL_ASSISTANT_ENABLED,
  EGlobalSearchGate.CALL_ASSISTANT_READ,
] as const;

// NOTE: NSAI-6588 — every page a user can navigate to, plus each tab of a
// tabbed page as its own entry ("page.<name>.<tab>") so "probes" lands on
// /analytics?tab=probes rather than on whatever tab the page opens first. A
// tab's `tab` param is the exact URL value the page reads (navigation-tabs.ts).
export const GLOBAL_SEARCH_PAGE_ENTRIES: readonly IGlobalSearchStaticEntry[] = [
  buildGlobalSearchPageEntry({
    id: "page.new_chat",
    titleKey: "sidebar:menu.new_chat",
    gates: [],
    target: { kind: EGlobalSearchTargetKind.NEW_CHAT, params: {} },
  }),
  buildGlobalSearchPageEntry({
    id: "page.chat_folders",
    titleKey: "sidebar:chat_folder_list.title",
    gates: [],
    target: { kind: EGlobalSearchTargetKind.CHAT_FOLDERS, params: {} },
  }),
  buildGlobalSearchPageEntry({
    id: "page.assistants",
    titleKey: "sidebar:menu.assistant",
    gates: [EGlobalSearchGate.ASSISTANT_READ],
    target: { kind: EGlobalSearchTargetKind.ASSISTANTS, params: {} },
  }),
  buildGlobalSearchPageEntry({
    id: "page.knowledge",
    titleKey: KNOWLEDGE_TITLE_KEY,
    gates: [EGlobalSearchGate.KNOWLEDGE_READ],
    target: { kind: EGlobalSearchTargetKind.KNOWLEDGE, params: {} },
  }),
  buildGlobalSearchPageEntry({
    id: "page.knowledge.my_files",
    titleKey: "knowledge:myFilesTab",
    subtitleKey: KNOWLEDGE_TITLE_KEY,
    gates: [EGlobalSearchGate.KNOWLEDGE_READ],
    target: {
      kind: EGlobalSearchTargetKind.KNOWLEDGE,
      params: { tab: KNOWLEDGE_TAB_VALUES.MY_FILES },
    },
  }),
  buildGlobalSearchPageEntry({
    id: "page.knowledge.shared_with_me",
    titleKey: "knowledge:sharedWithMeTab",
    subtitleKey: KNOWLEDGE_TITLE_KEY,
    gates: [EGlobalSearchGate.KNOWLEDGE_READ],
    target: {
      kind: EGlobalSearchTargetKind.KNOWLEDGE,
      params: { tab: KNOWLEDGE_TAB_VALUES.SHARED_WITH_ME },
    },
  }),
  buildGlobalSearchPageEntry({
    id: "page.doc_recognition",
    titleKey: "sidebar:menu.doc_recognition",
    gates: [
      EGlobalSearchGate.PLATFORM_WEB,
      EGlobalSearchGate.DOC_RECOGNITION_ENABLED,
      EGlobalSearchGate.DOC_RECOGNITION_READ,
    ],
    target: { kind: EGlobalSearchTargetKind.DOC_RECOGNITION, params: {} },
  }),
  buildGlobalSearchPageEntry({
    id: "page.skills",
    titleKey: "sidebar:menu.skills",
    gates: [EGlobalSearchGate.SKILLS_ENABLED, EGlobalSearchGate.SKILL_READ],
    target: { kind: EGlobalSearchTargetKind.SKILLS, params: {} },
  }),
  buildGlobalSearchPageEntry({
    id: "page.skill_catalog",
    titleKey: "skills:catalog.title",
    subtitleKey: "sidebar:menu.skills",
    gates: [
      EGlobalSearchGate.SKILLS_ENABLED,
      EGlobalSearchGate.SKILL_CATALOG_READ,
    ],
    target: { kind: EGlobalSearchTargetKind.SKILL_CATALOG, params: {} },
  }),
  buildGlobalSearchPageEntry({
    id: "page.generated_files",
    titleKey: "sidebar:menu.generated_files",
    gates: [],
    target: { kind: EGlobalSearchTargetKind.GENERATED_FILES, params: {} },
  }),
  buildGlobalSearchPageEntry({
    id: "page.agents",
    titleKey: AGENTS_TITLE_KEY,
    gates: AGENTS_GATES,
    target: { kind: EGlobalSearchTargetKind.AGENTS, params: {} },
  }),
  buildGlobalSearchPageEntry({
    id: "page.agents.scheduled",
    titleKey: "agents:tabs.scheduled",
    subtitleKey: AGENTS_TITLE_KEY,
    gates: AGENTS_GATES,
    target: {
      kind: EGlobalSearchTargetKind.AGENTS,
      params: { tab: AGENTS_TAB_VALUES.SCHEDULED },
    },
  }),
  buildGlobalSearchPageEntry({
    id: "page.agents.webhooks",
    titleKey: "agents:tabs.webhooks",
    subtitleKey: AGENTS_TITLE_KEY,
    gates: AGENTS_GATES,
    target: {
      kind: EGlobalSearchTargetKind.AGENTS,
      params: { tab: AGENTS_TAB_VALUES.WEBHOOKS },
    },
  }),
  buildGlobalSearchPageEntry({
    id: "page.agents.event_rules",
    titleKey: "agents:tabs.eventRules",
    subtitleKey: AGENTS_TITLE_KEY,
    gates: AGENTS_GATES,
    target: {
      kind: EGlobalSearchTargetKind.AGENTS,
      params: { tab: AGENTS_TAB_VALUES.EVENT_RULES },
    },
  }),
  buildGlobalSearchPageEntry({
    id: "page.agents.marketplace",
    titleKey: "agents:tabs.marketplace",
    subtitleKey: AGENTS_TITLE_KEY,
    gates: [...AGENTS_GATES, EGlobalSearchGate.MARKETPLACE_AUTOMATION_READ],
    target: {
      kind: EGlobalSearchTargetKind.AGENTS,
      params: { tab: AGENTS_TAB_VALUES.MARKETPLACE },
    },
  }),
  buildGlobalSearchPageEntry({
    id: "page.anonymization_lab",
    titleKey: "sidebar:menu.anonymization_lab",
    gates: [
      EGlobalSearchGate.PLATFORM_WEB,
      EGlobalSearchGate.ANONYMIZATION_LAB_ENABLED,
    ],
    target: { kind: EGlobalSearchTargetKind.ANONYMIZATION_LAB, params: {} },
  }),
  buildGlobalSearchPageEntry({
    id: "page.prompt_library",
    titleKey: "sidebar:menu.prompt_library",
    gates: [EGlobalSearchGate.PROMPT_LIBRARY_READ],
    target: { kind: EGlobalSearchTargetKind.PROMPT_LIBRARY, params: {} },
  }),
  buildGlobalSearchPageEntry({
    id: "page.models",
    titleKey: "sidebar:menu.models",
    gates: [EGlobalSearchGate.MODEL_READ],
    target: { kind: EGlobalSearchTargetKind.MODELS, params: {} },
  }),
  buildGlobalSearchPageEntry({
    id: "page.analytics",
    titleKey: ANALYTICS_TITLE_KEY,
    gates: ANALYTICS_GATES,
    target: { kind: EGlobalSearchTargetKind.ANALYTICS, params: {} },
  }),
  buildGlobalSearchPageEntry({
    id: "page.analytics.dashboard",
    titleKey: "analytics:dashboard",
    subtitleKey: ANALYTICS_TITLE_KEY,
    gates: [...ANALYTICS_GATES, EGlobalSearchGate.ANALYTICS_READ],
    target: {
      kind: EGlobalSearchTargetKind.ANALYTICS,
      params: { tab: ANALYTICS_TAB_VALUES.DASHBOARD },
    },
  }),
  buildGlobalSearchPageEntry({
    id: "page.analytics.messages",
    titleKey: "analytics:messages",
    subtitleKey: ANALYTICS_TITLE_KEY,
    gates: [...ANALYTICS_GATES, EGlobalSearchGate.ANALYTICS_READ],
    target: {
      kind: EGlobalSearchTargetKind.ANALYTICS,
      params: { tab: ANALYTICS_TAB_VALUES.MESSAGES },
    },
  }),
  buildGlobalSearchPageEntry({
    id: "page.analytics.probes",
    titleKey: "analytics:probes",
    subtitleKey: ANALYTICS_TITLE_KEY,
    gates: [...ANALYTICS_GATES, EGlobalSearchGate.PROBE_READ],
    target: {
      kind: EGlobalSearchTargetKind.ANALYTICS,
      params: { tab: ANALYTICS_TAB_VALUES.PROBES },
    },
  }),
  buildGlobalSearchPageEntry({
    id: "page.integrations",
    titleKey: INTEGRATIONS_TITLE_KEY,
    gates: [EGlobalSearchGate.INTEGRATIONS_PAGE],
    target: { kind: EGlobalSearchTargetKind.INTEGRATIONS, params: {} },
  }),
  buildGlobalSearchPageEntry({
    id: "page.integrations.all",
    titleKey: "integration:all",
    subtitleKey: INTEGRATIONS_TITLE_KEY,
    gates: [
      EGlobalSearchGate.INTEGRATIONS_PAGE,
      EGlobalSearchGate.INTEGRATION_READ,
    ],
    target: {
      kind: EGlobalSearchTargetKind.INTEGRATIONS,
      params: { tab: INTEGRATIONS_TAB_VALUES.ALL },
    },
  }),
  buildGlobalSearchPageEntry({
    id: "page.integrations.connected",
    titleKey: "integration:connected",
    subtitleKey: INTEGRATIONS_TITLE_KEY,
    gates: [
      EGlobalSearchGate.INTEGRATIONS_PAGE,
      EGlobalSearchGate.INTEGRATION_READ,
    ],
    target: {
      kind: EGlobalSearchTargetKind.INTEGRATIONS,
      params: { tab: INTEGRATIONS_TAB_VALUES.CONNECTED },
    },
  }),
  buildGlobalSearchPageEntry({
    id: "page.integrations.custom",
    titleKey: "customIntegration:title",
    subtitleKey: INTEGRATIONS_TITLE_KEY,
    gates: [
      EGlobalSearchGate.INTEGRATIONS_PAGE,
      EGlobalSearchGate.CUSTOM_INTEGRATION_ACCESS,
    ],
    target: {
      kind: EGlobalSearchTargetKind.INTEGRATIONS,
      params: { tab: INTEGRATIONS_TAB_VALUES.CUSTOM },
    },
  }),
  buildGlobalSearchPageEntry({
    id: "page.call_assistant",
    titleKey: "sidebar:menu.call_assistant",
    gates: CALL_ASSISTANT_GATES,
    target: { kind: EGlobalSearchTargetKind.CALL_ASSISTANT, params: {} },
  }),
  buildGlobalSearchPageEntry({
    id: "page.uploads",
    titleKey: "sidebar:menu.uploads",
    gates: CALL_ASSISTANT_GATES,
    target: { kind: EGlobalSearchTargetKind.UPLOADS, params: {} },
  }),
  buildGlobalSearchPageEntry({
    id: "page.faq",
    titleKey: "sidebar:menu.faq",
    gates: [],
    target: { kind: EGlobalSearchTargetKind.FAQ, params: {} },
  }),
  buildGlobalSearchPageEntry({
    id: "page.profile_settings",
    titleKey: "settings:sections.profile",
    gates: [],
    target: {
      kind: EGlobalSearchTargetKind.SETTINGS,
      params: { section: SETTINGS_SECTION_VALUES.PROFILE },
    },
  }),
  buildGlobalSearchPageEntry({
    id: "page.organization_settings",
    titleKey: "sidebar:menu.organizations",
    gates: [EGlobalSearchGate.IN_ORGANIZATION],
    target: {
      kind: EGlobalSearchTargetKind.SETTINGS,
      params: { section: SETTINGS_SECTION_VALUES.ORGANIZATION },
    },
  }),
];
